import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill';

export default () => (
  <SectionContainer
    id='universal-life-insurance-faq' // '-section-container' will be appended dynamically
    moduleName='faq-module'
    moduleVariation='A'>
    <Row align='center'>
      <Col
        align='center'
        medium={10}
        large={8}>
        <Typography
          component='h2'
          nextElement='typography'>
          Questions about universal life insurance&#63; <WidowBlock>We&#39;ve got answers.</WidowBlock>
        </Typography>
      </Col>
    </Row>
    <Row align='center'>
      <Col
        medium={10}
        large={8}>
        <Accordion
          id='faq-universal-life-insurance'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={[
            {
              id: 'universal-life-insurance-how-does-it-work',
              title: (<>How does universal life <WidowBlock>insurance work&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Universal life gives you protection for the people you love, as well as the flexibility to choose the amount of your death benefit, how much you pay in premiums, and when you pay them (within certain limits). Cash value earns interest every day, and expenses are taken out monthly. As long as there is enough cash value to pay your policy&#39;s monthly expenses, your coverage will continue. It will also build cash value that can be used for anything, anytime.<sup>2</sup>{' '}
                  <Link
                    id='faq-universal-life-insurance-how-does-it-work-link'
                    linkUrl='/life-and-money/how-universal-life-insurance-works/'
                    variant='primaryDeemphasize'>
                    Find out more about how Universal life works
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'universal-life-insurance-whole-life-comparison',
              title: (<>What are the differences between universal life insurance and whole <WidowBlock>life insurance&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography
                    disableBottomPadding
                    weight={400}>
                    How they&#39;re similar:
                  </Typography>
                  <BulletedList
                    bulletType='filledCircle'
                    bulletedListData={[
                      { bulletText: 'Both are types of permanent life insurance.' },
                      { bulletText: 'Both build cash value.' },
                    ]}
                  />
                  <Typography
                    disableBottomPadding
                    weight={400}>
                    How they&#39;re different:
                  </Typography>
                  <BulletedList
                    bulletType='filledCircle'
                    bulletedListData={[
                      {
                        bulletText: <>
                          <Link
                            id='universal-life-insurance-whole-life-comparison-death-benefit-link'
                            linkUrl='/life-insurance/whole-life-insurance/'
                            ariaLabel='See Whole Life Insurance death benefit difference'
                            variant='primaryDeemphasize'>
                            Whole life
                          </Link>
                          {' '}comes with a guaranteed death benefit, while universal life comes with a flexible death benefit.
                        </>,
                      },
                      {
                        bulletText: <>
                          <Link
                            id='universal-life-insurance-whole-life-comparison-flexible-premiums-link'
                            linkUrl='/life-insurance/whole-life-insurance/'
                            ariaLabel='See Whole Life Insurance premiums difference'
                            variant='primaryDeemphasize'>
                            Whole life
                          </Link>
                          {' '}comes with set premiums, while universal life comes with flexible premiums.
                        </>,
                      },
                    ]}
                  />
                  <Typography disableBottomPadding>
                    <Link
                      id='universal-life-insurance-whole-life-comparison-life-insurance-guide-link'
                      linkUrl='/life-and-money/life-insurance-guide/'
                      variant='primaryDeemphasize'>
                      See our guide to life insurance
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'universal-life-insurance-or-whole-life-which-is-better',
              title: (<>Which is better: universal or <WidowBlock>whole life&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  The best one for you depends on your goals. Both universal life and{' '}
                  <Link
                    id='universal-life-insurance-or-whole-life-whole-life-link'
                    linkUrl='/life-insurance/whole-life-insurance/'
                    variant='primaryDeemphasize'>
                    whole life insurance
                  </Link>
                  {' '}give you lifelong coverage<sup>3</sup> and build cash value over time that you can use for anything you need throughout your life.<sup>2</sup> Universal life comes with flexible premiums, while whole life comes with set premiums. Plus, whole life can earn dividends (while not guaranteed, we&#39;ve paid them every year since 1872) that can be taken as cash, used to pay premiums, or increase your coverage (and cash value). Find out which is right for you.{' '}
                  <Link
                    id='universal-life-insurance-or-whole-life-fafa-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    Connect with an advisor
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'universal-life-insurance-how-flexible-premiums-work',
              title: (<>How do flexible premiums work with a universal life <WidowBlock>insurance policy&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Universal life gives you flexibility to choose how much you pay in premiums and when you pay them &#40;within certain limits&#41; as your circumstances change. While you may eventually have to pay higher premiums to keep your coverage, that flexibility can make it easier to keep your insurance policy in force if your earnings vary like if you&#39;re self&#45;employed, own a business, or earn money through royalties. Ask an advisor if universal life is right for you.{' '}
                  <Link
                    id='universal-life-insurance-how-flexible-premiums-work-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    Connect with one today
                  </Link>.
                </Typography>
              ),
            },

            {
              id: 'universal-life-insurance-how-cash-value-universal-life-work',
              title: (<>How does the cash value of a universal life insurance <WidowBlock>policy work&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  You can take money out of your policy as a withdrawal up to the amount of the premium you&#39;ve paid in so far without paying taxes. You can also take a loan against your cash value or use the policy&#39;s cash value as collateral for a loan from a bank. If you choose to surrender your entire policy, you can take all of its cash value, but you&#39;ll also be surrendering your coverage.<sup>2</sup> One of our advisors can give you more details.{' '}
                  <Link
                    id='universal-life-insurance-how-cash-value-universal-life-work-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    Get matched with one today
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'universal-life-insurance-what-to-do-with-cash-value-universal-life',
              title: (<>What can I do with the cash value of my universal life <WidowBlock>insurance policy&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Your policy&#39;s cash value is an asset that you can use for anything, anytime. It can be used for unexpected emergencies, like a home repair or a business investment. You can take money out of your policy as a withdrawal up to the amount of the premium you&#39;ve paid in so far without paying taxes. You can also take a loan against your cash value or use the policy&#39;s cash value as collateral for a loan from a bank. If you choose to surrender your entire policy, you can take all of its cash value, but you&#39;ll also be surrendering your coverage.<sup>2</sup> Ready to get started on your policy&#63;{' '}
                  <Link
                    id='universal-life-insurance-what-to-do-with-cash-value-universal-life-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    Connect with an advisor
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'universal-life-insurance-universal-cash-value-at-policyowner-death',
              title: (<>What happens to a universal life policy&#39;s cash value at the <WidowBlock>policyholder&#39;s death&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  At the time of the insured&#39;s death, the beneficiary (or beneficiaries) will receive the death benefit and the insurance company will keep the account&#39;s remaining cash value. The beneficiary won&#39;t get any of the cash value because the policyowner can only use it while they&#39;re alive. In general, the value of the death benefit will be less if the policyowner withdrew or borrowed against the cash value at some point. Still have questions&#63;{' '}
                  <Link
                    id='universal-life-insurance-universal-cash-value-at-policyowner-death-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    Connect with an advisor
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'universal-life-insurance-universal-cash-value-after-death',
              title: (<>Can you cash out a universal life <WidowBlock>insurance policy&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Yes,{' '}
                  <Typography
                    component='span'
                    italic>
                    this pool
                  </Typography>
                  {' '}of money is called cash value. The cash value of a policy is an asset that you can use for anything, anytime. It can be used for unexpected emergencies, like a home repair or a business investment. You can withdraw money from a universal life insurance policy up to the amount of the premium you&#39;ve paid in so far without paying taxes. One of our advisors can give you more details.{' '}
                  <Link
                    id='universal-life-insurance-universal-cash-value-after-death-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    Get matched with one today
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'universal-life-insurance-can-you-borrow-from-it',
              title: (<>Can you borrow from a universal life <WidowBlock>insurance policy&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Yes. You can take a policy loan. Interest is charged on the loan, but there is no financial underwriting, so you can borrow at any time against your policy, and the repayment terms are flexible. You can repay on your own terms or allow the loan interest to be added to the loan. It&#39;s important to know that your death benefit will be reduced by the amount of the loan until it&#39;s repaid,<sup>2</sup> and the loan must be carefully managed to avoid any tax consequences. Want to find out more on borrowing against life insurance&#63;{' '}
                  <Link
                    id='universal-life-insurance-can-you-borrow-from-it-read-guide-link'
                    linkUrl='/life-and-money/borrowing-against-life-insurance/'
                    variant='primaryDeemphasize'>
                    Read our guide
                  </Link> or <Link
                    id='universal-life-insurance-can-you-borrow-from-it-connect-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    connect with an advisor
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'universal-life-insurance-can-universal-life-collect-dividends',
              title: (<>Can a universal life insurance policy <WidowBlock>collect dividends&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  No, it cannot. However,{' '}
                  <Link
                    id='universal-life-insurance-can-universal-life-collect-dividends-whole-life-link'
                    linkUrl='/life-insurance/whole-life-insurance/'
                    variant='primaryDeemphasize'>
                    whole life insurance
                  </Link>
                  {' '}does. While not guaranteed, we&#39;ve paid them every year since 1872. You can take the dividend as cash, use it to pay premiums, or buy more coverage. Ask an advisor which insurance is right for your needs and goals.{' '}
                  <Link
                    id='universal-life-insurance-can-universal-life-collect-dividends-fafa-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    Get matched today
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'universal-life-insurance-which-policy-is-right-for-me',
              title: (<>How do I know which life insurance policy is right <WidowBlock>for me&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  The best types of insurance for you and your loved ones will depend on your needs and goals. And if you&#39;re not sure what those are, that&#39;s ok.{' '}
                  <Link
                    id='universal-life-insurance-find-your-policy-fafa-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'>
                    Our advisors can help you figure it out
                  </Link>.
                  {' '}Beyond universal life insurance, there are three other types of life insurance that can give you the protection you need:{' '}
                  <Link
                    id='universal-life-insurance-term-life-link'
                    linkUrl='/life-insurance/term-life-insurance/'
                    variant='primaryDeemphasize'>
                    term life
                  </Link>
                  {' '}insurance,{' '}
                  <Link
                    id='universal-life-insurance-whole-life-insurance-link'
                    linkUrl='/life-insurance/whole-life-insurance/'
                    variant='primaryDeemphasize'>
                    whole life
                  </Link>
                  {' '}insurance, and{' '}
                  <Link
                    id='universal-life-insurance-variable-universal-life-insurance-link'
                    linkUrl='/life-insurance/variable-universal-life-insurance-vul/'
                    variant='primaryDeemphasize'>
                    variable universal life
                  </Link>
                  {' '}insurance.
                  {' '}
                  <Link
                    id='universal-life-insurance-compare-types-of-life-insurance-link'
                    linkUrl='/life-insurance/compare-types/'
                    variant='primaryDeemphasize'>
                    See how universal life compares to these coverage options
                  </Link>.
                </Typography>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
